import Nav from "./Nav";
import Footer from "./Footer";
import { t } from "../lib/localize";
import { A11Y } from "../content/a11y";
import React from "react";
import GlobalStyles from "../styles/Typography";
import LogoAnimation from "./LogoAnimation";
import styled, { keyframes } from "styled-components";
import { CartProvider } from "../contexts/CartContext";
import { LanguageProvider, LanguageContext } from "../contexts/LanguageContext";
import { IntroProvider, useIntro } from "../contexts/IntroContext";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import theme from "../styles/theme";
import wheelUrl from "../assets/images/wheel-corner.svg";
import { down, layout, fontSize, fontWeight } from "../styles/tokens";

const Shell = styled.div`
  display: flex;
  flex-direction: column;

  /* Fill the dynamic viewport on mobile Safari/Chrome */
  min-height: 100vh;   /* fallback */
  min-height: 100svh;  /* iOS small viewport */
  min-height: 100dvh;  /* dynamic viewport */

  position: relative;

  /* Avoid accidental horizontal scroll while letting vertical paint naturally */
  overflow-x: clip;
  overflow-y: visible;

  /* Keep a solid page background for stable mobile rendering */
  background: #fff;

  /* New stacking context so z-index:-1 backgrounds stay behind content */
  isolation: isolate;
`;

/* Overlay fade-out timed to coincide with the logo's own fade-out
   (LogoAnimation's 75→100% segment). Both fades run together over
   the last 300ms of the 1.2s intro, so the page underneath gradually
   reveals instead of popping into view when the overlay unmounts at
   introDone. The 0%/75% pair keeps the overlay fully opaque during
   the logo's fade-in + hold. Keystone (75%) must stay in sync with
   the matching 75% step in LogoAnimation.js. */
const overlayFadeOut = keyframes`
  0%, 75% { opacity: 1; }
  100%    { opacity: 0; }
`;

const IntroOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;            /* above everything */
  display: grid;
  place-items: center;
  background: #fff;         /* or transparent if your animation has a bg */
  pointer-events: auto;     /* blocks clicks to content while visible */
  animation: ${overlayFadeOut} 1.2s ease-out forwards;
`;

/** Fixed to the viewport, but forced behind via z-index:-1 */
const WheelOverlay = styled.img`
  position: fixed;
  right: 0;

  /* Keep pinned to the visual bottom edge; if env() is 0 it behaves like 0px */
  bottom: max(0px, env(safe-area-inset-bottom));

  width: clamp(420px, 60vw, 850px);
  height: auto;
  pointer-events: none;
  user-select: none;

  z-index: -1;           /* behind content inside Shell */
  transform: translateZ(0);
  will-change: transform;
`;

const GradientBlob = styled.div`
  position: fixed;
  left: 0;
  bottom: max(0px, env(safe-area-inset-bottom));
  width: clamp(520px, 55vw, 900px);
  height: clamp(520px, 55vw, 900px);
  pointer-events: none;
  user-select: none;
  z-index: -1;                 /* behind content & footer */

  /* Soft violet glow + optional subtle vertical lines */
  background:
    /* layer 1: subtle vertical pinstripes (optional; comment out if not desired) */
    repeating-linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.18) 0 1px,  /* light line */
      rgba(255, 255, 255, 0.00) 1px 4px /* gap */
    ),
    /* layer 2: main soft blob */
    radial-gradient(
      140% 140% at 10% 90%,             /* anchor near bottom-left */
      rgba(124, 118, 199, 0.65) 0%,     /* inner lavender */
      rgba(129, 138, 217, 0.35) 30%,
      rgba(167, 177, 242, 0.18) 55%,
      rgba(255, 255, 255, 0.0) 72%      /* feather to transparent */
    );

  /* Gentle vignette so it blends into the white */
  mask-image: radial-gradient(130% 130% at 10% 90%, black 45%, transparent 80%);
  -webkit-mask-image: radial-gradient(130% 130% at 10% 90%, black 45%, transparent 80%);

  /* Mobile GPU hint */
  transform: translateZ(0);
`;

const ContentContainer = styled.main`
  display: flex;
  flex-direction: column;

  /* Fill remaining space reliably on mobile browsers */
  flex: 1 0 auto;
  min-height: 0;

  position: relative;  /* stays above the wheel automatically due to z-index stacking */
  z-index: 0;

  /* Consistent breathing room between every page's last content block
     and the footer, sourced from layout.pageBottom so individual pages
     don't hand-tune their own bottom margins. Safe-area inset stacks on
     top for iOS home-indicator clearance. The $overlay branch (home
     page) zeroes the padding because the footer is position:fixed over
     the hero video — no flowing content below to breathe against. */
  padding-bottom: ${({ $overlay }) => $overlay
    ? '0'
    : `calc(${layout.pageBottom} + max(0px, env(safe-area-inset-bottom)))`};

  /* Zero out any trailing margin-bottom on the chain of last-children so
     a page's own internal spacing doesn't stack on top of pageBottom and
     make the gap to the footer page-dependent. Five levels deep handles
     every current page structure. Skipped on the home page ($overlay)
     because the hero's VideoBackground uses its own margin-bottom to
     create the clean 14px white frame around the video — zeroing that
     would collapse the frame into a full-bleed video. */
  ${({ $overlay }) => !$overlay && `
    & > *:last-child,
    & > *:last-child > *:last-child,
    & > *:last-child > *:last-child > *:last-child,
    & > *:last-child > *:last-child > *:last-child > *:last-child,
    & > *:last-child > *:last-child > *:last-child > *:last-child > *:last-child {
      margin-bottom: 0 !important;
    }
  `}
`;

const SkipToContent = styled.a`
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  z-index: 100000;

  &:focus {
    position: fixed;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    height: auto;
    overflow: visible;
    padding: 12px 24px;
    background: rgba(4, 2, 64, 0.95);
    color: #fff;
    font-size: ${fontSize.base};
    font-weight: ${fontWeight.semibold};
    border-radius: 30px;
    text-decoration: none;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
    outline: none;
  }
`;

const NavSpacer = styled.div`
  /* Matches Toolbar height and avoids the notch/URL bar overlap on iOS */
  height: calc(75px + env(safe-area-inset-top));
  ${down('mobile')} {
    height: calc(60px + env(safe-area-inset-top));
  }
`;

/* Rendered as a child of LanguageProvider rather than inline in Layout's
   body, because Layout is the component that renders the provider — its
   own body sits outside the context it creates. Same reason
   IntroOverlayGate below is a separate component. */
function SkipLink() {
  const { language } = React.useContext(LanguageContext) || {};
  return (
    <SkipToContent href="#main-content">{t(A11Y.skipToContent, language)}</SkipToContent>
  );
}

/* The intro overlay is a child of IntroProvider so it can read context. */
function IntroOverlayGate() {
  const { introDone } = useIntro();
  if (introDone) return null;
  return (
    <IntroOverlay aria-hidden="true">
      <LogoAnimation />
    </IntroOverlay>
  );
}

export default function Layout({ children, location }) {
  /* Derived, not stateful — location is passed by Gatsby on every render
     (both SSR and client nav), so computing directly avoids the extra
     render + missing-deps useEffect that lived here before. */
  const isIndex = location?.pathname === "/";

  /* Track internal navigation history in sessionStorage as a full STACK
     of visited paths so back buttons can walk multiple levels deep
     instead of falling through to `navigate(-1)` (which uses the
     browser history stack including external origins, and may exit
     the site entirely if the user entered via a direct deep link).

     Direction detection: on every route change, if the new path matches
     the entry below the stack's current top, the user just went back
     (either via the in-site back button or the browser back button) —
     pop the top instead of pushing. Otherwise, push the new path.
     This means the in-site back button doesn't need to set a flag,
     and the browser back button is handled automatically.

     First-mount handling: sessionStorage persists across reloads and
     returns to the site within the same tab, so on the first render
     we RESET the stack to just the current path. Without this, a user
     landing on a deep URL after closing and reopening the tab would
     inherit stale navigation history that points to URLs they never
     visited this session. */
  const isFirstMount = React.useRef(true);
  React.useEffect(() => {
    if (typeof window === "undefined" || !location?.pathname) return;
    const current = location.pathname + (location.search || "");

    if (isFirstMount.current) {
      isFirstMount.current = false;
      sessionStorage.setItem("soq:navStack", JSON.stringify([current]));
      return;
    }

    let stack = [];
    try {
      stack = JSON.parse(sessionStorage.getItem("soq:navStack") || "[]");
    } catch (_) {
      stack = [];
    }

    if (stack.length >= 2 && stack[stack.length - 2] === current) {
      // Back navigation: pop the top (the page we just left).
      stack.pop();
    } else if (stack[stack.length - 1] !== current) {
      // Forward navigation: push the new path. The duplicate-guard
      // catches edge cases where the effect fires twice for the
      // same path (e.g. some query-param-only changes).
      stack.push(current);
    }

    sessionStorage.setItem("soq:navStack", JSON.stringify(stack));
  }, [location?.pathname, location?.search]);

  return (
  <Shell>
    {/* decorative bg only when not on index */}
    {!isIndex && (
      <>
        <GradientBlob />
        <WheelOverlay src={wheelUrl} alt="" aria-hidden="true" />
      </>
    )}

    <GlobalStyles />

    {/* ✅ Keep providers and MAIN always rendered (SSR includes <main>) */}
    <MuiThemeProvider theme={theme}>
      <StyledThemeProvider theme={theme}>
        <CartProvider>
          <LanguageProvider>
            <IntroProvider>

              <SkipLink />
              <Nav />
              <NavSpacer />

              {/* ✅ This is your single main landmark */}
              <ContentContainer id="main-content" $overlay={isIndex}>
                {children}
              </ContentContainer>

              <Footer overlay={isIndex} />

              {/* Intro overlay reads `introDone` from IntroProvider so it
                  stays in sync with whatever hero reveals are gating on. */}
              <IntroOverlayGate />

            </IntroProvider>
          </LanguageProvider>
        </CartProvider>
      </StyledThemeProvider>
    </MuiThemeProvider>
  </Shell>
);
}